import React, { useState } from 'react';
import { CalendarCheck } from 'lucide-react';
import { freeFetch, formatDate, inputClass, labelClass } from './api';
import { HealthConsent } from './HealthConsent';

export default function WeeklyCheckIn({ me, onSubmitted }) {
  const [sessions, setSessions] = useState('');
  const [energy, setEnergy] = useState('Good');
  const [obstacles, setObstacles] = useState('');
  const [healthConsent, setHealthConsent] = useState(Boolean(me.health_consent));
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const onConsentChange = (granted) => {
    setHealthConsent(granted);
    if (!granted) setObstacles('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const data = await freeFetch('/checkins', {
        method: 'POST',
        body: JSON.stringify({
          sessions_completed: Number(sessions),
          energy,
          obstacles: healthConsent ? obstacles : '',
        }),
      });
      setMessage(data.message);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="border border-mono-line bg-mono-card p-8" data-testid="free-weekly-checkin">
      <div className="flex items-center gap-3 mb-2">
        <CalendarCheck className="h-6 w-6 text-mono-mute" />
        <h2 className="font-heading text-lg font-bold uppercase tracking-wider text-white">Weekly Check-In</h2>
      </div>

      {message ? (
        <p className="text-white font-body mt-2" data-testid="free-checkin-confirmation">
          {message}
        </p>
      ) : !me.first_session_date ? (
        <p className="text-mono-mute font-body text-sm mt-2" data-testid="free-checkin-locked">
          Check-ins begin one week after your first logged session.
        </p>
      ) : !me.checkin_due ? (
        <p className="text-mono-mute font-body text-sm mt-2" data-testid="free-checkin-not-due">
          Next check-in: {formatDate(me.next_checkin_date)}
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-5 mt-4" data-testid="free-checkin-form">
          <div>
            <label className={labelClass}>How many sessions did you complete this week?</label>
            <input
              type="number"
              min="0"
              max="7"
              required
              value={sessions}
              onChange={(e) => setSessions(e.target.value)}
              className={inputClass}
              data-testid="checkin-sessions-input"
            />
          </div>
          <div>
            <label className={labelClass}>How is your energy and recovery feeling right now?</label>
            <select value={energy} onChange={(e) => setEnergy(e.target.value)} className={inputClass} data-testid="checkin-energy-select">
              {['Great', 'Good', 'Average', 'Tired', 'Burnt out'].map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>
          <div>
            <HealthConsent granted={healthConsent} onChange={onConsentChange} testid="checkin-health-consent" />
            <label className={labelClass}>Anything getting in the way of showing up consistently? (optional)</label>
            <textarea
              value={obstacles}
              onChange={(e) => setObstacles(e.target.value)}
              maxLength={300}
              rows={3}
              disabled={!healthConsent}
              className={`${inputClass} ph-no-capture disabled:opacity-40 disabled:cursor-not-allowed`}
              data-testid="checkin-obstacles-input"
            />
          </div>
          {error && <p className="text-red-400 text-sm font-body" data-testid="checkin-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="bg-white hover:bg-[#E5E5E5] disabled:opacity-50 text-black font-heading font-bold uppercase tracking-wider rounded-none px-8 py-3 transition-colors"
            data-testid="checkin-submit-btn"
          >
            Submit check-in
          </button>
        </form>
      )}
    </div>
  );
}
